<template>
  <div class="task-detail-header-description">
    <!-- Description Editor -->
    <template v-if="messageEditing">
      <editor
        ref="editor"
        v-model="internalDescription"
        :mention-users="mentionUsers"
        :existing-attachments="internalAttachments"
        :clear-attachments="clearAttachments"
        :saved-templates="savedTemplates"
        :autofocus="autoFocusEditor"
        allows-attachment
        allows-google-drive
        name="message"
        placeholder="Write your message..."
        @mentions-list="insertMentions"
        @files-list="addFileHandler"
        @file-list-removed="removeFileHandler"
        @cleared-attachments="clearAttachments = false"
      />
      <div class="task-detail-header-description__actions">
        <dd-button type="text" color="grey" size="medium" @click="undoUpdates">Cancel</dd-button>
        <dd-button :color="ColorAssistant.ProjectColor" size="medium" @click="submitUpdates">Save Changes</dd-button>
      </div>
    </template>
    <!-- DESCRIPTION -->
    <template v-else>
      <!-- DESCRIPTION / ORIGINAL EMAIL TABS (only when the task was created from an email) -->
      <div v-if="hasSourceEmail" class="task-detail-header-description__tabs">
        <dd-button
          :class="{ 'task-detail-header-description__tab--active': !showingOriginalEmail }"
          size="small"
          type="text"
          class="task-detail-header-description__tab"
          @click="showingOriginalEmail = false"
        >Description</dd-button>
        <dd-button
          :class="{ 'task-detail-header-description__tab--active': showingOriginalEmail }"
          size="small"
          type="text"
          class="task-detail-header-description__tab"
          @click="showingOriginalEmail = true"
        >Original Email</dd-button>
      </div>
      <!-- ORIGINAL EMAIL -->
      <div v-if="hasSourceEmail && showingOriginalEmail" class="task-detail-header-description__source-email">
        <iframe
          :src="sourceEmailLink"
          sandbox="allow-popups allow-popups-to-escape-sandbox"
          frameborder="0"
          scrolling="auto"
        />
      </div>
      <template v-else>
        <div
          v-if="hasDescription"
          class="task-detail-header-description__view"
          @click.exact="handleDescriptionClick"
        >
          <div class="user-inputed-rich-text" v-html="internalDescription" />
        </div>
        <!-- No description state-->
        <div v-else class="task-detail-header-description__empty">
          <dd-button
            v-if="!isDisabled"
            type="flat"
            size="small"
            @click="focusEditor"
          >Add a description&hellip;</dd-button>
        </div>
      </template>
      <!-- Initial Attachments -->
      <div v-if="hasAttachments" class="task-detail-header-description__attachments-wrapper">
        <attachments-list :attachments="initialAttachments" />
      </div>
    </template>
  </div>
</template>

<script lang="ts">
/* General */
import { defineComponent } from 'vue'

/* Mixins */

/* Components */

/* Helpers */
import ColorAssistant from '@/helpers/color-assistant'

/* Data types */
import { IAttachment } from '@/types/data-objects/attachments'
import {
  IAtMentionUser,
  IDescriptionTemplate,
} from '@/api/types/response.types'

/* API */

export default defineComponent({
  name: 'TaskDetailHeaderMessage',

  props: {
    description: {
      type: String,
      required: true,
    },
    sourceEmailLink: {
      type: String,
      default: null,
    },
    initialAttachments: {
      type: Array as () => IAttachment[],
      default: (): IAttachment[] => [],
    },
    mentionUsers: {
      type: Array as () => IAtMentionUser[],
      required: true,
    },
    savedTemplates: {
      type: Array as () => IDescriptionTemplate[],
      default: (): IDescriptionTemplate[] => [],
    },
    isDisabled: {
      type: Boolean,
      default: false,
    },
  },

  emits: ['update-description', 'update:initialAttachments'],

  data() {
    return {
      ColorAssistant,

      internalDescription: '',
      removedAttachments: [] as any[],
      listFileUploadIDs: [] as any[],
      listAtMentionIDs: [] as any[],

      clearAttachments: false,
      messageEditing: false,
      autoFocusEditor: false,
      // Always start on the text description; the source email is opt-in per visit.
      showingOriginalEmail: false,
    }
  },

  computed: {
    hasAttachments(): boolean {
      return this.initialAttachments.length > 0
    },
    hasSourceEmail(): boolean {
      return this.sourceEmailLink !== null && this.sourceEmailLink.trim() !== ''
    },
    hasDescription(): boolean {
      if (
        this.internalDescription === '<p><br></p>' ||
        this.internalDescription === null ||
        this.internalDescription === ''
      ) {
        return false
      } else {
        return true
      }
    },

    internalAttachments: {
      get(): any {
        return this.initialAttachments
      },
      set(newValue: any) {
        this.$emit('update:initialAttachments', newValue)
      },
    },
  },

  watch: {
    description: {
      immediate: true,
      handler(newVal: string) {
        this.internalDescription = newVal
      },
    },
  },

  methods: {
    /**
     * Handle click on message div to avoid edit when clicking on links
     */
    async handleDescriptionClick(event: any) {
      if (this.isDisabled) {
        return
      }

      // Verify if there's no text selected
      const isHighlightingText =
        window
          .getSelection()
          .toString()
          .trim() !== ''

      const isClickingLink = event.target.tagName.toUpperCase() === 'A'

      if (!(isClickingLink || isHighlightingText)) {
        this.focusEditor()
      }
    },

    async focusEditor() {
      this.showingOriginalEmail = false
      this.messageEditing = true
      this.autoFocusEditor = true
    },

    addFileHandler(files: IAttachment[]) {
      this.listFileUploadIDs = files.map(item => item.id)
    },

    /**
     * Remove file from FormData
     */
    removeFileHandler(id: number) {
      const file = this.initialAttachments.find(item => item.id === id)

      if (file && file.status !== 'ready') {
        this.removedAttachments.push(id)
      } else {
        this.listFileUploadIDs = this.listFileUploadIDs.filter(
          item => item === id,
        )
        this.internalAttachments = this.internalAttachments.filter(
          (item: any) => item.id === id,
        )
      }
    },

    insertMentions(matches: any) {
      this.listAtMentionIDs = matches
    },

    undoUpdates() {
      this.messageEditing = false
      this.internalDescription = this.description
      this.resetMetaData()
    },

    submitUpdates() {
      this.messageEditing = false
      if (
        this.description !== this.internalDescription ||
        (this.removedAttachments && this.removedAttachments.length > 0) ||
        (this.listFileUploadIDs && this.listFileUploadIDs.length > 0)
      ) {
        const remove =
          this.removedAttachments.length > 0 ? this.removedAttachments : null
        const upload =
          this.listFileUploadIDs.length > 0 ? this.listFileUploadIDs : null

        this.$emit('update-description', {
          description: this.internalDescription,
          listAtMentionIDs: this.listAtMentionIDs,
          listFileUploadIDs: upload,
          listRemovedAttachmentIDs: remove,
        })

        this.resetMetaData()
      }
    },

    resetMetaData() {
      this.listAtMentionIDs = []
      this.listFileUploadIDs = []
      this.removedAttachments = []
      this.clearAttachments = true
    },
  },
})
</script>

<style lang="scss">
.task-detail-header-description {
  $side-padding: 10px;
  width: calc(100% + (2 * $side-padding));
  margin-left: -1 * $side-padding;
  margin-top: 10px;

  .description {
    margin-bottom: 32px;

    &::after {
      position: absolute;
      right: 0;
      bottom: -16px;
      left: 0;
      height: 1px;
      content: '';
      background: $silver-two;
    }
  }

  &__tabs {
    display: flex;
    padding: 20px $side-padding 12px;
    margin-bottom: 12px;
    border-bottom: $border--solid-light;
  }

  &__tab {
    position: relative;
    padding: 0;
    margin-right: 24px;
    @include lettering--p-sm;
    font-weight: $bold;

    &--active.dd-button--text {
      color: var(--color-text-secondary);

      &::after {
        position: absolute;
        right: 0;
        bottom: -13px;
        left: 0;
        height: 2px;
        content: '';
        background: $color--blue;
        border-radius: 1px;
      }
    }
  }

  &__source-email {
    padding: 0 $side-padding;
    margin-bottom: 12px;

    iframe {
      width: 100%;
      border: $border--solid-extra-light;
      border-radius: 4px;
      min-height: 500px;
    }
  }

  &__empty {
    display: flex;
    justify-content: center;
    align-items: center;
    border: dashed 1px $color--gray-45;
    border-radius: 10px;
    height: 180px;
    margin: 0 10px;

    .dd-button {
      font-size: 14px;
      font-weight: normal;
      line-height: 1.71;
    }
  }

  &__view {
    padding: 12px $side-padding;
    margin-bottom: 12px;

    /* Add background on editor hover when toolbar is hidden */
    &:hover {
      @include media-breakpoint-up(md) {
        border-radius: 8px;
        background-color: var(--color-background-secondary);
      }
    }
  }

  &__attachments-wrapper {
    padding: 0 $side-padding;
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    margin: 10px 0;
  }
}
</style>
